import type React from "react";
import type { ChangeEvent } from "react";
import { useCallback, useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import type { BaseTextFieldProps } from "@mui/material/TextField/TextField";
import { debounce } from "@mui/material/utils";
import { useTheme } from "@mui/material/styles";

interface SearchBarProps extends BaseTextFieldProps {
  // eslint-disable-next-line no-unused-vars
  onSearch: (searchText: string) => void;
  debounceDelay?: number;
  fullWidth?: boolean;
  sx?: object;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  debounceDelay = 400,
  fullWidth = false,
  sx = {},
}: SearchBarProps) => {
  const [inputValue, setInputValue] = useState<string>("");

  const handleOnChange = useCallback(
    debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Do not trigger the search if the input was changed by selection
      onSearch(event.target.value ? event.target.value : "");
    }, debounceDelay),
    [onSearch],
  );

  const handleClear = () => {
    setInputValue("");
    onSearch("");
  };
  const theme = useTheme();
  return (
    <TextField
      placeholder="Search..."
      variant="outlined"
      data-testid={"searchBar"}
      size="small"
      fullWidth={fullWidth}
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
        handleOnChange(e);
      }}
      //TODO: Move me to the theme?
      sx={{ borderRadius: 1, ...sx }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" data-testid={"searchIcon"} sx={{ color: theme.palette.text.primary }} />
            </InputAdornment>
          ),
          endAdornment: inputValue && (
            <InputAdornment position="end">
              <IconButton onClick={handleClear} data-testid={"clearSearchButton"} size="small">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default SearchBar;
